import { useEffect, useRef, useState, useMemo } from "react";
import MDBox from "components/MDBox";
import { useMaterialUIController } from "context";
import DataTable, { createTheme } from "react-data-table-component";
import { getColumns, Attributes, operationURLS } from "./model";
import CTLAdvancedFilter from "application/components/CTLAdvancedFilter";
import CTLBaseDialogForm from "application/components/CTLBaseDialogForm";
import { CTLDialogPaper } from "application/components/styles/CTLDialogContent";
import MDSnackbar from "components/MDSnackbar";
import DeleteConfirmation from "application/components/DeleteConfirmation";
import CTLNotification from "application/components/CTLNotification";
import { CircularProgress, Fade, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip } from "@mui/material";
import { makeStyles } from "@material-ui/core";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { telemetry } from "globals/endpoints";
import UploadFileDialog from "./UploadFileDialog";
import UploadSampleFileDialog from "./UploadSampleFileDialog";
import FullConfigDialog from "./ConfigDialog";
import XmlEditorDialog from "./XmlEditorDialog";
import loggerFactory from "globals/logger/logger-factory";
import AuthenticationService from "application/modules/authentication/AuthenticationService";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AppSettingsAltIcon from "@mui/icons-material/AppSettingsAlt";
import SecurityUpdateGoodIcon from "@mui/icons-material/SecurityUpdateGood";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PreviewIcon from "@mui/icons-material/Preview";
import Tooltip from "@mui/material/Tooltip";
import HtmlIcon from "@mui/icons-material/Html";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import GridOnIcon from "@mui/icons-material/GridOn";
import DataObjectIcon from "@mui/icons-material/DataObject";
import CodeIcon from "@mui/icons-material/Code";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import { jsPDF as JsPDF } from "jspdf";
import "jspdf-autotable";

const useStyles = makeStyles((theme) => ({
  loadingContainer: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: theme.zIndex.modal + 1,
  },
}));

function Provisioning({
  onLoad,
  pageId,
  searchText,
  registerCallBacks,
  pageName,
}) {
  const [controller, dispatch] = useMaterialUIController();
  const { activePage } = controller;
  const classes = useStyles();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmMultipleDelete, setConfirmMultipleDelete] = useState(false);
  const [selectedRow, setSelectedRow] = useState([]);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [updateList, setUpdateList] = useState(Date.now());
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const darkModeContext = useMaterialUIController();
  const [deviceData, setDeviceData] = useState();
  const [metadata, setMetaData] = useState();
  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);
  const [openMetaFrom, setOpenMetaForm] = useState(false);
  const [getMacAddress, setMacAddress] = useState("");
  const [openUploadFile, setOpenUploadFile] = useState(false);
  const [openUploadSampleFile, setOpenUploadSampleFile] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingSampleFile, setUploadingSampleFile] = useState(false);
  const [rowData, setRowData] = useState("");
  const [openFullConfigDialog, setOpenFullConfigDialog] = useState(false);
  const [openXmlDialog, setOpenXmlDialog] = useState(false); // #9420
  const [xmlMac, setXmlMac] = useState(""); // #9420
  const [originalMacAddress, setOriginalMacAddress] = useState("");
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [newMacAddress, setNewMacAddress] = useState("");
  const [editManufacturer, setEditManufacturer] = useState("");
  const [editModel, setEditModel] = useState("");
  const selectedRows = useRef([]);
  const filteredDataRef = useRef([]);
  const [ViewData, setViewData] = useState([]);
  const [openDownloadDialog, setOpenDownloadDialog] = useState(false);
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });

  useEffect(() => {
    if (Attributes) {
      const isViewOnly = AuthenticationService.isViewOnlyUser();
      onLoad({
        ...Attributes,
        enableAddButton: Attributes.enableAddButton && !isViewOnly,
        enableMultipleDeleteButton:
          Attributes.enableMultipleDeleteButton && !isViewOnly,
        enableUploadFaxButton: Attributes.enableUploadFaxButton && !isViewOnly,
        enableUploadButton: Attributes.enableUploadButton && !isViewOnly,
      });
    }
    registerCallBacks &&
      registerCallBacks({
        openAdvancedFilter: onOpenFilter,
        openForm: onOpenForm,
        onUploadFax: onUploadFax,
        onUpload: onUploadSampleFile,
        onMultipleDelete: onMultipleDelete,
        DownloadData: onDownload,
      });
  }, [activePage]);

  useEffect(() => {
    GetData();
  }, [updateList]);

  function GetData() {
    telemetry
      .post(operationURLS.list)
      .then((res) => {
        setViewData(res.data.data);
      })
      .catch((error) => {
        console.log("error while the call deviceManager/getall ", error);
      });
  }

  // #9420: apply free-text search (searchText) + advanced filters to the device list (client-side).
  const filteredData = useMemo(() => {
    let rows = Array.isArray(ViewData) ? ViewData : [];
    const term = (searchText || "").trim().toLowerCase();
    if (term) {
      rows = rows.filter((r) =>
        ["macAddress", "extensionNumber", "model", "manufacturer"].some((k) =>
          String(r?.[k] ?? "").toLowerCase().includes(term)
        )
      );
    }
    if (Array.isArray(advancedFilters) && advancedFilters.length) {
      rows = rows.filter((r) =>
        advancedFilters.every((f) => {
          const val = String(f?.value ?? "").toLowerCase();
          if (!val) return true;
          const cell = String(r?.[f.fieldName] ?? "").toLowerCase();
          if (f.dataType === "number" || f.dataType === "date") {
            const c = Number(r?.[f.fieldName]);
            const v = Number(f.value);
            if (Number.isNaN(c) || Number.isNaN(v)) return cell.includes(val);
            return f.direction === "to" ? c <= v : c >= v;
          }
          return cell.includes(val);
        })
      );
    }
    return rows;
  }, [ViewData, searchText, advancedFilters]);

  // #9420: keep ref in sync so the toolbar-registered onDownload always sees latest filtered rows
  useEffect(() => {
    filteredDataRef.current = filteredData;
  }, [filteredData]);

  function onOpenFilter() {
    setOpenFilter(true);
  }

  // #9420: build a colourful, self-contained HTML report of all devices and trigger a download
  const escapeHtml = (val) =>
    String(val ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const manufacturerColor = (m) => {
    const key = String(m ?? "").trim().toLowerCase();
    if (key === "cisco") return "#1BA0D7";
    if (key === "coral") return "#FF7043";
    if (key === "polycom") return "#6A1B9A";
    if (key === "yealink") return "#2E7D32";
    if (key === "grandstream") return "#EF6C00";
    return "#546E7A";
  };

  // toolbar-registered callback: open the format-picker dialog
  const onDownload = () => setOpenDownloadDialog(true);

  const getExportRows = () =>
    (Array.isArray(filteredDataRef.current) ? filteredDataRef.current : []).map((r) => ({
      macAddress: r?.macAddress || "",
      extensionNumber: r?.extensionNumber || "",
      model: r?.model || "",
      manufacturer: r?.manufacturer || "",
      manualEdit: r?.xmlManuallyEdited ? "Yes" : "No",
    }));

  const fileTimestamp = () => new Date().toISOString().replace(/[:.]/g, "-");

  const saveBlob = (blob, filename) => {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const notifyDownloaded = (format, count) => {
    setNotify({
      isOpen: true,
      message: "",
      type: "success",
      pagename: pageName,
      status: `Downloaded ${count} device${count === 1 ? "" : "s"} as ${format}`,
    });
  };

  const downloadAsHtml = () => {
    const rows = getExportRows();
    const generatedOn = new Date().toLocaleString();
    const totalDevices = rows.length;
    const manuallyEditedCount = rows.filter((r) => r.manualEdit === "Yes").length;
    const manufacturers = Array.from(
      new Set(rows.map((r) => r.manufacturer.trim()).filter(Boolean))
    );

    const bodyRows = rows
      .map((r, i) => {
        const mColor = manufacturerColor(r.manufacturer);
        const manualBadge =
          r.manualEdit === "Yes"
            ? `<span class="badge badge-warning">Manually Edited</span>`
            : `<span class="muted">-</span>`;
        return `
          <tr>
            <td class="sno">${i + 1}</td>
            <td class="mono">${escapeHtml(r.macAddress || "N/A")}</td>
            <td><span class="pill pill-ext">${escapeHtml(r.extensionNumber || "N/A")}</span></td>
            <td>${escapeHtml(r.model || "N/A")}</td>
            <td><span class="pill" style="background:${mColor}1A;color:${mColor};border:1px solid ${mColor}55;">${escapeHtml(r.manufacturer || "N/A")}</span></td>
            <td class="center">${manualBadge}</td>
          </tr>`;
      })
      .join("");

    const manufacturerChips = manufacturers
      .map((m) => {
        const c = manufacturerColor(m);
        return `<span class="pill" style="background:${c}1A;color:${c};border:1px solid ${c}55;">${escapeHtml(m)}</span>`;
      })
      .join(" ");

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<title>Provisioning Devices Report</title>
<style>
  * { box-sizing: border-box; }
  body {
    margin: 0;
    font-family: "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    background: linear-gradient(135deg, #f5f7fa 0%, #e4ecf7 100%);
    color: #263238;
    padding: 24px;
  }
  .wrap { max-width: 1200px; margin: 0 auto; }
  .header {
    background: linear-gradient(135deg, #1976d2 0%, #42a5f5 50%, #26c6da 100%);
    color: #fff;
    border-radius: 14px;
    padding: 24px 28px;
    box-shadow: 0 10px 30px rgba(25, 118, 210, 0.25);
    margin-bottom: 20px;
  }
  .header h1 { margin: 0 0 6px 0; font-size: 26px; letter-spacing: 0.3px; }
  .header .sub { opacity: 0.92; font-size: 13px; }
  .cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 14px;
    margin-bottom: 20px;
  }
  .card {
    background: #fff;
    border-radius: 12px;
    padding: 16px 18px;
    box-shadow: 0 4px 14px rgba(0,0,0,0.06);
    border-left: 5px solid #1976d2;
  }
  .card.green { border-left-color: #2e7d32; }
  .card.orange { border-left-color: #ef6c00; }
  .card.purple { border-left-color: #6a1b9a; }
  .card .label { font-size: 12px; color: #607d8b; text-transform: uppercase; letter-spacing: 0.6px; }
  .card .value { font-size: 26px; font-weight: 700; margin-top: 4px; color: #263238; }
  .card .chips { margin-top: 8px; display: flex; flex-wrap: wrap; gap: 6px; }
  .table-wrap { background: #fff; border-radius: 12px; overflow: hidden; box-shadow: 0 6px 20px rgba(0,0,0,0.08); }
  table { width: 100%; border-collapse: collapse; }
  thead th {
    background: linear-gradient(135deg, #263238 0%, #37474f 100%);
    color: #fff;
    text-align: left;
    padding: 12px 14px;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.7px;
  }
  thead th.center, td.center { text-align: center; }
  tbody td { padding: 12px 14px; border-bottom: 1px solid #eceff1; font-size: 14px; vertical-align: middle; }
  tbody tr:nth-child(even) { background: #fafbfd; }
  tbody tr:hover { background: #e3f2fd; }
  td.sno { font-weight: 600; color: #455a64; width: 60px; }
  td.mono { font-family: "SFMono-Regular", Menlo, Consolas, monospace; color: #1565c0; }
  .pill { display: inline-block; padding: 4px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; }
  .pill-ext { background: #e8f5e9; color: #2e7d32; border: 1px solid #a5d6a7; }
  .badge { display: inline-block; padding: 4px 10px; border-radius: 6px; font-size: 11px; font-weight: 700; letter-spacing: 0.4px; }
  .badge-warning { background: #fff3e0; color: #e65100; border: 1px solid #ffb74d; }
  .muted { color: #b0bec5; }
  .empty { text-align: center; padding: 40px; color: #90a4ae; font-style: italic; }
  .footer { margin-top: 18px; text-align: center; color: #78909c; font-size: 12px; }
  @media print {
    body { background: #fff; }
    .header { box-shadow: none; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .card, .table-wrap { box-shadow: none; }
    tbody tr:hover { background: transparent; }
  }
</style>
</head>
<body>
  <div class="wrap">
    <div class="header">
      <h1>Provisioning Devices Report</h1>
      <div class="sub">Generated on ${escapeHtml(generatedOn)}</div>
    </div>
    <div class="cards">
      <div class="card"><div class="label">Total Devices</div><div class="value">${totalDevices}</div></div>
      <div class="card orange"><div class="label">Manually Edited</div><div class="value">${manuallyEditedCount}</div></div>
      <div class="card green">
        <div class="label">Manufacturers</div>
        <div class="value">${manufacturers.length}</div>
        <div class="chips">${manufacturerChips || '<span class="muted">&mdash;</span>'}</div>
      </div>
    </div>
    <div class="table-wrap">
      <table>
        <thead>
          <tr><th>S.No</th><th>MAC Address</th><th>Extension</th><th>Model</th><th>Manufacturer</th><th class="center">Manual Edit</th></tr>
        </thead>
        <tbody>${bodyRows || `<tr><td colspan="6" class="empty">No devices to display.</td></tr>`}</tbody>
      </table>
    </div>
    <div class="footer">Coral Telecom &middot; Provisioning Export</div>
  </div>
</body>
</html>`;

    saveBlob(new Blob([html], { type: "text/html;charset=utf-8" }), `Provisioning-Devices-${fileTimestamp()}.html`);
    notifyDownloaded("HTML", totalDevices);
  };

  const csvCell = (val) => {
    const s = String(val ?? "");
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const downloadAsCsv = () => {
    const rows = getExportRows();
    const header = ["S.No", "MAC Address", "Extension", "Model", "Manufacturer", "Manual Edit"];
    const lines = [header.map(csvCell).join(",")];
    rows.forEach((r, i) => {
      lines.push(
        [i + 1, r.macAddress, r.extensionNumber, r.model, r.manufacturer, r.manualEdit]
          .map(csvCell)
          .join(",")
      );
    });
    // Add UTF-8 BOM so Excel detects encoding correctly
    const csv = "﻿" + lines.join("\r\n");
    saveBlob(new Blob([csv], { type: "text/csv;charset=utf-8" }), `Provisioning-Devices-${fileTimestamp()}.csv`);
    notifyDownloaded("CSV", rows.length);
  };

  const downloadAsPdf = () => {
    const rows = getExportRows();
    const doc = new JsPDF({ orientation: "landscape" });
    const pageWidth = doc.internal.pageSize.getWidth();

    // colourful header band
    doc.setFillColor(25, 118, 210);
    doc.rect(0, 0, pageWidth, 22, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.text("Provisioning Devices Report", 14, 14);
    doc.setFontSize(10);
    doc.text(new Date().toLocaleString(), pageWidth - 14, 14, { align: "right" });

    doc.setTextColor(38, 50, 56);
    doc.setFontSize(11);
    doc.text(`Total devices: ${rows.length}`, 14, 30);

    doc.autoTable({
      startY: 36,
      head: [["S.No", "MAC Address", "Extension", "Model", "Manufacturer", "Manual Edit"]],
      body: rows.map((r, i) => [
        i + 1,
        r.macAddress || "N/A",
        r.extensionNumber || "N/A",
        r.model || "N/A",
        r.manufacturer || "N/A",
        r.manualEdit,
      ]),
      headStyles: { fillColor: [38, 50, 56], textColor: [255, 255, 255], fontStyle: "bold" },
      alternateRowStyles: { fillColor: [250, 251, 253] },
      styles: { fontSize: 10, cellPadding: 3 },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 4) {
          const c = manufacturerColor(data.cell.raw);
          const hex = c.replace("#", "");
          const r = parseInt(hex.slice(0, 2), 16);
          const g = parseInt(hex.slice(2, 4), 16);
          const b = parseInt(hex.slice(4, 6), 16);
          data.cell.styles.textColor = [r, g, b];
          data.cell.styles.fontStyle = "bold";
        }
        if (data.section === "body" && data.column.index === 5 && data.cell.raw === "Yes") {
          data.cell.styles.fillColor = [255, 243, 224];
          data.cell.styles.textColor = [230, 81, 0];
          data.cell.styles.fontStyle = "bold";
        }
      },
    });

    doc.save(`Provisioning-Devices-${fileTimestamp()}.pdf`);
    notifyDownloaded("PDF", rows.length);
  };

  const xmlEscape = (val) =>
    String(val ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&apos;");

  const downloadAsExcel = () => {
    const rows = getExportRows();
    const headers = ["S.No", "MAC Address", "Extension", "Model", "Manufacturer", "Manual Edit"];

    const headerRow =
      "<Row>" +
      headers
        .map(
          (h) =>
            `<Cell ss:StyleID="hdr"><Data ss:Type="String">${xmlEscape(h)}</Data></Cell>`
        )
        .join("") +
      "</Row>";

    const bodyRows = rows
      .map((r, i) => {
        const cells = [
          { v: i + 1, t: "Number" },
          { v: r.macAddress, t: "String" },
          { v: r.extensionNumber, t: "String" },
          { v: r.model, t: "String" },
          { v: r.manufacturer, t: "String" },
          { v: r.manualEdit, t: "String", style: r.manualEdit === "Yes" ? "warn" : null },
        ];
        return (
          "<Row>" +
          cells
            .map(
              (c) =>
                `<Cell${c.style ? ` ss:StyleID="${c.style}"` : ""}><Data ss:Type="${c.t}">${xmlEscape(c.v)}</Data></Cell>`
            )
            .join("") +
          "</Row>"
        );
      })
      .join("");

    const xml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <Styles>
  <Style ss:ID="hdr">
   <Font ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#263238" ss:Pattern="Solid"/>
   <Alignment ss:Horizontal="Center"/>
  </Style>
  <Style ss:ID="warn">
   <Font ss:Bold="1" ss:Color="#E65100"/>
   <Interior ss:Color="#FFF3E0" ss:Pattern="Solid"/>
  </Style>
 </Styles>
 <Worksheet ss:Name="Devices">
  <Table>
   <Column ss:Width="40"/>
   <Column ss:Width="130"/>
   <Column ss:Width="80"/>
   <Column ss:Width="110"/>
   <Column ss:Width="110"/>
   <Column ss:Width="90"/>
   ${headerRow}
   ${bodyRows}
  </Table>
 </Worksheet>
</Workbook>`;

    // SpreadsheetML 2003 — Excel opens cleanly when saved with .xls extension
    saveBlob(
      new Blob([xml], { type: "application/vnd.ms-excel;charset=utf-8" }),
      `Provisioning-Devices-${fileTimestamp()}.xls`
    );
    notifyDownloaded("Excel", rows.length);
  };

  const downloadAsJson = () => {
    const rows = getExportRows();
    const payload = {
      generatedOn: new Date().toISOString(),
      total: rows.length,
      devices: rows.map((r, i) => ({
        sno: i + 1,
        macAddress: r.macAddress,
        extensionNumber: r.extensionNumber,
        model: r.model,
        manufacturer: r.manufacturer,
        manualEdit: r.manualEdit === "Yes",
      })),
    };
    saveBlob(
      new Blob([JSON.stringify(payload, null, 2)], { type: "application/json;charset=utf-8" }),
      `Provisioning-Devices-${fileTimestamp()}.json`
    );
    notifyDownloaded("JSON", rows.length);
  };

  const downloadAsXml = () => {
    const rows = getExportRows();
    const body = rows
      .map(
        (r, i) =>
          `  <device sno="${i + 1}">
    <macAddress>${xmlEscape(r.macAddress)}</macAddress>
    <extensionNumber>${xmlEscape(r.extensionNumber)}</extensionNumber>
    <model>${xmlEscape(r.model)}</model>
    <manufacturer>${xmlEscape(r.manufacturer)}</manufacturer>
    <manualEdit>${r.manualEdit === "Yes" ? "true" : "false"}</manualEdit>
  </device>`
      )
      .join("\n");
    const xml = `<?xml version="1.0" encoding="UTF-8"?>
<devices generatedOn="${new Date().toISOString()}" total="${rows.length}">
${body}
</devices>`;
    saveBlob(
      new Blob([xml], { type: "application/xml;charset=utf-8" }),
      `Provisioning-Devices-${fileTimestamp()}.xml`
    );
    notifyDownloaded("XML", rows.length);
  };

  const downloadAsTxt = () => {
    const rows = getExportRows();
    const cols = [
      { key: "sno", header: "S.No", width: 5 },
      { key: "macAddress", header: "MAC Address", width: 20 },
      { key: "extensionNumber", header: "Extension", width: 12 },
      { key: "model", header: "Model", width: 14 },
      { key: "manufacturer", header: "Manufacturer", width: 16 },
      { key: "manualEdit", header: "Manual Edit", width: 12 },
    ];
    const pad = (val, w) => String(val ?? "").padEnd(w).slice(0, w);
    const sep = cols.map((c) => "-".repeat(c.width)).join("  ");
    const headerLine = cols.map((c) => pad(c.header, c.width)).join("  ");
    const lines = [
      "Provisioning Devices Report",
      `Generated: ${new Date().toLocaleString()}`,
      `Total devices: ${rows.length}`,
      "",
      headerLine,
      sep,
    ];
    rows.forEach((r, i) => {
      lines.push(
        cols
          .map((c) => pad(c.key === "sno" ? i + 1 : r[c.key], c.width))
          .join("  ")
      );
    });
    saveBlob(
      new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" }),
      `Provisioning-Devices-${fileTimestamp()}.txt`
    );
    notifyDownloaded("TXT", rows.length);
  };

  const downloadFormats = [
    { key: "excel", label: "Excel", ext: ".xls", color: "#1B5E20", icon: <GridOnIcon />, handler: downloadAsExcel },
    { key: "csv", label: "CSV", ext: ".csv", color: "#2E7D32", icon: <DescriptionIcon />, handler: downloadAsCsv },
    { key: "pdf", label: "PDF", ext: ".pdf", color: "#C62828", icon: <PictureAsPdfIcon />, handler: downloadAsPdf },
    { key: "html", label: "HTML", ext: ".html", color: "#1565C0", icon: <HtmlIcon />, handler: downloadAsHtml },
    { key: "json", label: "JSON", ext: ".json", color: "#EF6C00", icon: <DataObjectIcon />, handler: downloadAsJson },
    { key: "xml", label: "XML", ext: ".xml", color: "#6A1B9A", icon: <CodeIcon />, handler: downloadAsXml },
    { key: "txt", label: "TXT", ext: ".txt", color: "#455A64", icon: <TextSnippetIcon />, handler: downloadAsTxt },
  ];

  const pickFormat = (handler) => {
    setOpenDownloadDialog(false);
    setTimeout(handler, 0);
  };

  function onOpenForm() {
    setFormData({
      eventTime: Date.now(),
    });
    setOpenForm(true);
  }

  function onCloseFilter() {
    setOpenFilter(false);
  }

  function onApplyFilter(advancedFilters) {
    setAdvancedFilters(advancedFilters);
    setOpenFilter(false);
  }

  const onSelectionChange = (ids) => {
    const selected = Array.isArray(ids) ? ids : [ids];
    selectedRows.current = selected;
  };

  const editRow = (row) => {
    setOriginalMacAddress(row.macAddress || "");
    setNewMacAddress("");
    setEditManufacturer(row.manufacturer || "");
    setEditModel(row.Model || "");
    setOpenEditDialog(true);
  };

  const deleteRow = (row) => {
    setSelectedRow(row);
    setConfirmDelete(true);
  };

  const onCancel = () => {
    setSelectedRow([]);
    setConfirmDelete(false);
    setConfirmMultipleDelete(false);
  };

  const onUploadFax = () => {
    setOpenUploadFile(true);
  };

  const onUploadSampleFile = () => {
    setOpenUploadSampleFile(true);
  };

  const handleUploadSampleFile = async (selectedFile) => {
    try {
      setUploadingSampleFile(true);

      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await telemetry.post(
        "deviceManager/upload/sample-file",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      setNotify({
        isOpen: true,
        message: "",
        type: "success",
        pagename: pageName,
        status: response?.data?.message || "File uploaded successfully",
      });
    } catch (error) {
      setNotify({
        isOpen: true,
        message: "Upload Failed",
        type: "error",
        pagename: pageName,
        status: error?.response?.data?.message || "Failed to upload sample file",
      });
    } finally {
      setUploadingSampleFile(false);
      setOpenUploadSampleFile(false);
    }
  };

  const handleUploadFile = async (selectedFile) => {
    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("peer", "file");
      formData.append("file", selectedFile);

      const response = await telemetry.post(
        "SCADAAsset/uploadCSVStatus",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      const resp = response.data;

      // Correct API condition
      if (resp.status !== 0) {
        setNotify({
          isOpen: true,
          message: resp.message,
          type: "error",
          pagename: pageName,
          status: "Upload Failed",
        });
        setUpdateList(Date.now());
        setUploading(false);
        setOpenUploadFile(false);
        return;
      }

      const successRows = resp.data.successReport?.rows || [];
      const errorRows = resp.data.errorReport?.rows || [];

      // Build success section
      const successSection = [
        "[SUCCESS REPORT]",
        "Device-Type,NodeName,IP,MAC-Address,Category,Status",
        ...successRows.map(
          (r) =>
            `${r.deviceType},${r.nodeName},${r.ip},${r.mac},${r.category},SUCCESS`,
        ),
      ].join("\n");

      // Build error section
      const errorSection = [
        "",
        "[ERROR REPORT]",
        "Device-Type,NodeName,IP,MAC-Address,Category,Reason",
        ...errorRows.map(
          (r) =>
            `${r.deviceType},${r.nodeName},${r.ip},${r.mac},${r.category},${r.reason}`,
        ),
      ].join("\n");

      const finalCSV = successSection + "\n\n" + errorSection;

      // Download CSV
      const blob = new Blob([finalCSV], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "Upload_CSV_Report.csv";
      link.click();

      setNotify({
        isOpen: true,
        type: "success",
        pagename: pageName,
        message: resp.message,
        status: `Total Success: ${resp.data.successReport.totalSuccess},
Total Failed: ${resp.data.errorReport.totalFailed}`,
      });
      setUpdateList(Date.now());
      setUploading(false);
      setOpenUploadFile(false);
    } catch (error) {
      setUploading(false);
      setOpenUploadFile(false);
      setUpdateList(Date.now());
      setNotify({
        isOpen: true,
        message: "Upload Failed",
        type: "error",
        pagename: pageName,
        status: error?.response?.data?.message || "API Error",
      });
    }
  };

  const onMultipleDelete = () => {
    if (selectedRows.current.length != 0) {
      setConfirmMultipleDelete(true);
    } else {
      setNotify({
        isOpen: true,
        message: "Please select at least one row to delete.",
        type: "warning",
        pagename: pageName,
        status: "No rows selected for deletion.",
      });
    }
  };

  const onMultipleDeleteAgree = () => {
    deleteAsset(selectedRows.current);
  };

  const onAgree = (row) => {
    deleteAsset([row.macAddress]);
  };

  const deleteAsset = (mac) => {
    if (!mac) {
      setNotify({
        isOpen: true,
        message: "Error",
        type: "warning",
        pagename: pageName,
        status: "MAC Address is missing",
      });
      return;
    }

    telemetry
      .delete(`${operationURLS.delete}/${mac}`)
      .then((response) => {
        setUpdateList(Date.now());
        setNotify({
          isOpen: true,
          message: "",
          type: "success",
          pagename: pageName,
          status: response?.data?.message || "Deleted successfully",
        });
      })
      .catch((error) => {
        setNotify({
          isOpen: true,
          message: error?.response?.data?.message || "Error",
          type: "error",
          pagename: pageName,
          status:
            error?.response?.data?.messageDetail || "Failed to delete device",
        });
      })
      .finally(() => {
        selectedRows.current = [];
        setSelectedRow([]);
        setConfirmDelete(false);
        setConfirmMultipleDelete(false);
      });
  };

  function onSaveForm(data) {
    telemetry
      .post(`${operationURLS.create}/${data.macAddress}`, data)
      .then((response) => {
        setUpdateList(Date.now());
        setNotify({
          isOpen: true,
          message: "",
          type: "success",
          pagename: pageName,
          status: response.data.messageDetail,
        });
      })
      .catch((error) => {
        openErrorSB();
      });
    setOpenForm(false);
  }

  function onSaveEdit() {
    telemetry
      .put(`${operationURLS.update}/${originalMacAddress}`, {
        newMacAddress: newMacAddress ? newMacAddress : "",
        manufacturer: editManufacturer ? editManufacturer : "",
        model: editModel ? editModel : "",
      })
      .then((response) => {
        setUpdateList(Date.now());
        setOriginalMacAddress("");
        setNewMacAddress("");
        setEditManufacturer("");
        setEditModel("");
        setOpenEditDialog(false);
        setNotify({
          isOpen: true,
          message: "",
          type: "success",
          pagename: pageName,
          status: response.data.messageDetail,
        });
      })
      .catch((error) => {
        console.error("Error updating data:", error);
        setNotify({
          isOpen: true,
          message: "Error",
          type: "error",
          pagename: pageName,
          status: error?.response?.data?.messageDetail || "Failed to update MAC Address",
        });
      });
  }

  function onCloseForm() {
    setOpenForm(false);
  }

  function onCloseMetaForm() {
    setOpenMetaForm(false);
  }

  function onSaveMetaForm(data) {
    telemetry
      .post(`deviceManager/create/${getMacAddress}`, data)
      .then((response) => {
        setOpenMetaForm(false);
        setNotify({
          isOpen: true,
          message: response.data.status,
          type: "success",
          pagename: pageName,
          status: response.data.message,
        });
      })
      .catch((error) => {
        setOpenMetaForm(false);
        setNotify({
          isOpen: true,
          message: error.response.message,
          type: "error",
          pagename: pageName,
          status: error.response.messageDetail,
        });
      });
  }

  const onUpdateConfiguration = (row) => {
    setOpenFullConfigDialog(true);
    setRowData(row);
  };

  // #9420: open the generated-XML preview / manual-override / reset editor
  const onEditXml = (row) => {
    setXmlMac(row?.macAddress);
    setOpenXmlDialog(true);
  };

  const onUpdateConfigurationSubmit = async (configData) => {
    console.log("Configuration Data Submitted: ", configData);
    setLoading(true);

    if (!configData?.macAddress) {
      setNotify({
        isOpen: true,
        message: "",
        type: "warning",
        status: "MAC Address not found!",
      });

      setLoading(false);
      return;
    }

    try {
      const response = await telemetry.post(
        `deviceManager/update/configuration/${configData.macAddress}`,
        configData,
      );
      setNotify({
        isOpen: true,
        message: response.data?.status || "Success",
        type: "success",
        pagename: pageName,
        status: response.data?.message || "Configuration updated successfully",
      });
    } catch (error) {
      setNotify({
        isOpen: true,
        message: error?.response?.data?.message || "Error",
        type: "error",
        pagename: pageName,
        status:
          error?.response?.data?.messageDetail ||
          "Something went wrong. Please try again.",
      });
    } finally {
      setLoading(false);
      setOpenFullConfigDialog(false);
    }
  };

  const onUpdateFirmware = (row) => {
    if (row.macAddress) {
      telemetry
        .get(`deviceManager/updateFirmware/${row.macAddress}`)
        .then((response) => {
          setMetaData(response.data.data);
          setOpenMetaForm(true);
          setNotify({
            isOpen: true,
            message: response.data.status,
            type: "info",
            pagename: pageName,
            status: response.data.message,
          });
        })
        .catch((error) => {
          setNotify({
            isOpen: true,
            message: error.response.data.message,
            type: "info",
            pagename: pageName,
            status: error.response.messageDetail,
          });
        });
    }
  };

  const onReboot = (row) => {
    if (row.macAddress) {
      telemetry
        .get(`deviceManager/reboot/${row.macAddress}`)
        .then((response) => {
          setMetaData(response.data.data);
          setOpenMetaForm(true);
          setNotify({
            isOpen: true,
            message: response.data.status,
            type: "info",
            pagename: pageName,
            status: response.data.message,
          });
        })
        .catch((error) => {
          setNotify({
            isOpen: true,
            message: error.response.data.message,
            type: "info",
            pagename: pageName,
            status: error.response.messageDetail,
          });
        });
    }
  };

  const onReset = (row) => {
    if (row.macAddress) {
      telemetry
        .get(`deviceManager/reset/${row.macAddress}`)
        .then((response) => {
          setMetaData(response.data.data);
          setOpenMetaForm(true);
          setNotify({
            isOpen: true,
            message: response.data.status,
            type: "info",
            pagename: pageName,
            status: response.data.message,
          });
        })
        .catch((error) => {
          setNotify({
            isOpen: true,
            message: error.response.message,
            type: "info",
            pagename: pageName,
            status: error.response.messageDetail,
          });
        });
    }
  };

  const onDeployConfiguration = (row) => {
    if (row.extensionNumber) {
      telemetry
        .post(`deviceManager/get/configuration/uc/${row.extensionNumber}`)
        .then((response) => {
          const resData = response.data.data;

          if (resData?.macAddress && resData.macAddress !== row.macAddress) {
            setNotify({
              isOpen: true,
              message: "MAC Address Mismatch",
              type: "warning",
              pagename: pageName,
              status: `Expected: ${row.macAddress}, Received: ${resData.macAddress}`,
            });
            return;
          }

          setMetaData(resData);
          setOpenMetaForm(true);
          setNotify({
            isOpen: true,
            message: response.data.status,
            type: "info",
            pagename: pageName,
            status: response.data.message,
          });
        })
        .catch((error) => {
          setNotify({
            isOpen: true,
            message: error?.response?.data?.message || "Error",
            type: "error",
            pagename: pageName,
            status: error?.response?.data?.messageDetail || "Failed to fetch configuration",
          });
        });
    }
  };

  const renderErrorSB = (
    <MDSnackbar
      color="error"
      icon="warning"
      title={`${pageName}`}
      content={`${pageName} ${Attributes.messages.error}`}
      open={errorSB}
      onClose={closeErrorSB}
      close={closeErrorSB}
    />
  );

  const renderSuccessSB = (
    <MDSnackbar
      color="success"
      icon="check"
      title={`${pageName}`}
      content={`${pageName} ${Attributes.messages.success}`}
      open={successSB}
      onClose={closeSuccessSB}
      close={closeSuccessSB}
    />
  );

  return (
    <MDBox>
      <MDBox
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          position: "relative",
        }}
      >
        <Fade in={loading}>
          <div className={classes.loadingContainer}>
            <MDTypography variant="body1">
              <CircularProgress color="info" /> &nbsp; Loading...
            </MDTypography>
          </div>
        </Fade>
      </MDBox>

      <DeleteConfirmation
        entityName="Asset"
        data={selectedRow}
        columns={getColumns()}
        confirmDelete={confirmDelete}
        onAgree={onAgree}
        onCancel={onCancel}
      />

      <DeleteConfirmation
        entityName="Asset"
        data={selectedRow}
        columns={getColumns()}
        confirmDelete={confirmMultipleDelete}
        onAgree={onMultipleDeleteAgree}
        onCancel={onCancel}
      />

      <CTLBaseDialogForm
        entityName="Asset"
        columns={getColumns()}
        openForm={openForm}
        onSaveForm={onSaveForm}
        onCloseForm={onCloseForm}
        formData={formData}
        pageAttributes={Attributes}
      />

      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: (theme) =>
            CTLDialogPaper(theme, { darkMode: darkModeContext[0].darkMode }),
        }}
      >
        <DialogTitle>Edit Device</DialogTitle>
        <DialogContent>
          <MDBox mb={2} mt={1}>
            <MDTypography variant="body2" fontWeight="medium">
              MAC Address: &nbsp;{originalMacAddress}
            </MDTypography>
          </MDBox>
          <TextField
            autoFocus
            fullWidth
            label="New MAC Address"
            value={newMacAddress}
            onChange={(e) => setNewMacAddress(e.target.value)}
          />
          <TextField
            fullWidth
            label="Manufacturer"
            value={editManufacturer}
            onChange={(e) => setEditManufacturer(e.target.value)}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label="Model"
            value={editModel}
            onChange={(e) => setEditModel(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ gap: 1 }}>
          <MDButton
            variant="outlined"
            color="dark"
            onClick={() => {
              setOpenEditDialog(false);
              setNewMacAddress("");
              setOriginalMacAddress("");
              setEditManufacturer("");
              setEditModel("");
            }}
          >
            Cancel
          </MDButton>
          <MDButton variant="contained" color="primary" onClick={onSaveEdit}>
            Apply
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* #9420: download-format picker — Excel / CSV / PDF / HTML / JSON / XML / TXT */}
      <Dialog
        open={openDownloadDialog}
        onClose={() => setOpenDownloadDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: (theme) =>
            CTLDialogPaper(theme, { darkMode: darkModeContext[0].darkMode }),
        }}
      >
        <DialogTitle>Download Devices &mdash; Choose Format</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text" sx={{ mb: 2 }}>
            {`Exporting ${(filteredDataRef.current || []).length} device${
              (filteredDataRef.current || []).length === 1 ? "" : "s"
            } (current filter applied).`}
          </MDTypography>
          <MDBox
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
              gap: 1.5,
            }}
          >
            {downloadFormats.map((fmt) => (
              <MDBox
                key={fmt.key}
                onClick={() => pickFormat(fmt.handler)}
                sx={{
                  cursor: "pointer",
                  borderRadius: "12px",
                  padding: "16px 12px",
                  textAlign: "center",
                  border: `2px solid ${fmt.color}33`,
                  background: `${fmt.color}0D`,
                  transition: "all 0.18s ease",
                  "&:hover": {
                    background: `${fmt.color}1F`,
                    borderColor: fmt.color,
                    transform: "translateY(-2px)",
                    boxShadow: `0 6px 16px ${fmt.color}33`,
                  },
                }}
              >
                <MDBox sx={{ color: fmt.color, fontSize: "32px", lineHeight: 1, mb: 0.5 }}>
                  {fmt.icon}
                </MDBox>
                <MDTypography variant="button" fontWeight="bold" sx={{ color: fmt.color }}>
                  {fmt.label}
                </MDTypography>
                <MDTypography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                  {fmt.ext}
                </MDTypography>
              </MDBox>
            ))}
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ gap: 1 }}>
          <MDButton
            variant="outlined"
            color="dark"
            onClick={() => setOpenDownloadDialog(false)}
          >
            Cancel
          </MDButton>
        </DialogActions>
      </Dialog>

      {metadata && metadata.form && (
        <CTLBaseDialogForm
          entityName="Device Configuration"
          columns={metadata.form.content}
          openForm={openMetaFrom}
          onSaveForm={onSaveMetaForm}
          onCloseForm={onCloseMetaForm}
          formData={deviceData || []}
          pageAttributes={{
            columns: 3,
            tabs: metadata?.form.tabs || [],
          }}
        />
      )}

      <CTLAdvancedFilter
        entityName="Asset"
        columns={getColumns()}
        applyFilter={openFilter}
        onApplyFilter={onApplyFilter}
        onCloseFilter={onCloseFilter}
      />

      <DataTable
        columns={[
          {
            name: "S.No",
            selector: (row, index) => index + 1,
            width: "80px",
            center: true,
          },
          {
            name: "Mac Address",
            selector: (row) => row?.macAddress || "N/A",
            sortable: true,
            grow: 3,
            style: {
              justifyContent: "flex-start",
            },
          },
          {
            name: "Extension",
            selector: (row) => row?.extensionNumber || "N/A",
            sortable: true,
            grow: 2,
            style: {
              justifyContent: "flex-start",
            },
          },
          {
            name: "Model",
            selector: (row) => row?.model || "N/A",
            sortable: true,
            grow: 2,
            style: {
              justifyContent: "flex-start",
            },
          },
          {
            name: "Manufacturer",
            selector: (row) => row?.manufacturer || "N/A",
            sortable: true,
            grow: 2,
            style: {
              justifyContent: "flex-start",
            },
          },
          {
            // #9420: highlight manually-edited (bulk-protected) profiles
            name: "Manual Edit",
            cell: (row) =>
              row?.xmlManuallyEdited ? (
                <Chip label="Manually Edited" color="warning" size="small" />
              ) : (
                "-"
              ),
            grow: 2,
            center: true,
          },
          {
            name: "Action",
            cell: (row) => (
              <MDBox display="flex" gap={1}>
                {!AuthenticationService.isViewOnlyUser() && (
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      color="success"
                      onClick={() => editRow(row)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                {!AuthenticationService.isViewOnlyUser() && (
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => deleteRow(row)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip title="Update Config">
                  <IconButton
                    size="small"
                    color="info"
                    onClick={() => onUpdateConfiguration(row)}
                  >
                    <AppSettingsAltIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* #9420: view / edit generated SEP XML, save manual override, reset */}
                <Tooltip title="View / Edit XML">
                  <IconButton
                    size="small"
                    color="info"
                    onClick={() => onEditXml(row)}
                  >
                    <PreviewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Deploy Firmware">
                  <IconButton
                    size="small"
                    color="warning"
                    onClick={() => onUpdateFirmware(row)}
                  >
                    <SecurityUpdateGoodIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Reboot">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => onReboot(row)}
                  >
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </MDBox>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "280px",
            center: true,
          },
        ]}
        data={filteredData}
        canSearch
        responsive
        pagination
        striped
        paginationPerPage={20}
        paginationRowsPerPageOptions={[5, 10, 20]}
        theme={darkModeContext[0].darkMode ? "modifiedDarkTheme" : "default"}
        customStyles={{
          headCells: {
            style: {
              paddingLeft: "16px",
              paddingRight: "16px",
            },
          },
          cells: {
            style: {
              paddingLeft: "16px",
              paddingRight: "16px",
            },
          },
        }}
        fixedHeader
        persistTableHead
        fixedHeaderScrollHeight={`${(window.innerHeight / 100) * 66}px`}
      />

      {renderSuccessSB}
      {renderErrorSB}

      <UploadFileDialog
        open={openUploadFile}
        setOpen={setOpenUploadFile}
        onUpload={handleUploadFile}
        uploading={uploading}
      />

      <UploadSampleFileDialog
        open={openUploadSampleFile}
        setOpen={setOpenUploadSampleFile}
        onUpload={handleUploadSampleFile}
        uploading={uploadingSampleFile}
      />

      <FullConfigDialog
        open={openFullConfigDialog}
        onClose={() => setOpenFullConfigDialog(false)}
        onSubmit={onUpdateConfigurationSubmit}
        rowData={rowData}
      />

      <XmlEditorDialog
        open={openXmlDialog}
        onClose={() => setOpenXmlDialog(false)}
        macAddress={xmlMac}
        onSaved={(msg) => {
          setUpdateList(Date.now());
          setNotify({
            isOpen: true,
            message: msg,
            type: "success",
            pagename: pageName,
            status: msg,
          });
        }}
      />

      <CTLNotification notify={notify} setNotify={setNotify} />
    </MDBox>
  );
}
export default Provisioning;
